import { MOBILE_SECTION_TABS_THEME_VARS } from "@circle-react-shared/uikit/Header/headerThemeVars";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useIsCustomColorTheme } from "@circle-react/hooks/useIsCustomColorTheme";
import { MobileShell } from "@circleco/compass/components/MobileShell";
import { Tabs } from "@circleco/compass/components/Tabs";
import { useLocation } from "@tanstack/react-router";

import { useSitePageMetadata } from "@/react/routes/Main/useSitePageMetadata";

import { useMobileChromeVisibility } from "../hooks/chrome/useMobileChromeVisibility";
import { useMobileTabsOverride } from "../hooks/sectionTabs/useMobileTabsOverride";
import { DefaultSectionTabs } from "./DefaultSectionTabs";

const DEFAULT_TAB_ROUTES = [
  "/feed",
  "/spaces",
  "/courses",
  "/events",
  "/members",
  "/leaderboard",
];

const EXCLUDED_TAB_ROUTES = ["settings/invitation_links"];

/**
 * Horizontal scrollable section tabs for mobile.
 *
 * Tabs are **hidden by default**. They appear in four cases:
 * 1. A page explicitly provides tabs via `useMobileTabsOverride()` (usually
 *    through `useRegisterMobileSectionTabs`/`useRegisterSettingsTabsOverride`)
 *    (e.g. Notifications → Inbox/Mentions, Messages → Inbox/Unread).
 * 2. The user is on a top-level section page (/feed, /spaces, /courses, …),
 *    where the global navigation tabs (Home, Spaces, Courses, …) are shown.
 * 3. The user is viewing a custom Site Page (landing page). These pages
 *    aren't in DEFAULT_TAB_ROUTES since their slugs are arbitrary, but they
 *    should still show the global nav tabs - otherwise mobile visitors get
 *    no nav links at all (the top bar only shows logo + auth actions for
 *    logged-out/non-member visitors, see MobileLoggedOutTopBar).
 *
 *    Note: this component doesn't check the page's `hide_header` setting
 *    directly - when that's on, `SitePageRoute` hides the entire top area
 *    (TopBar + this component) via `useMobileHideTopNav`, so this branch
 *    never gets a chance to render in that case. See `SitePageRoute.tsx`.
 * 4. A logged-out visitor or non-member is viewing a space (`/c/...`), e.g.
 *    when a space is the community's logged-out default page. Same reason as
 *    above: their top bar has no navigation, so without the tabs they can't
 *    reach Spaces (or anything else). Members navigate via the drawer instead.
 *
 * Each default tab renders as a TanStack Router `<Link>` via the
 * Compass Tabs navigation extension.
 */
export const MobileSectionTabs = () => {
  const { tabsOverride: override } = useMobileTabsOverride();
  const pathname = useLocation({ select: location => location.pathname });
  const { isSitePageRoute } = useSitePageMetadata();
  const { currentCommunityMember, isLoading } = usePunditUserContext();
  const { isTopBarUnbranded } = useMobileChromeVisibility();
  // Default theme opts out of community branding entirely,
  // falling back to Compass's own default tokens
  const isCustomColorTheme = useIsCustomColorTheme();

  const isVisitorOnSpaceRoute =
    !isLoading && !currentCommunityMember && pathname.startsWith("/c/");

  const shouldShowDefaultTabs =
    (DEFAULT_TAB_ROUTES.some(prefix => pathname.startsWith(prefix)) ||
      isSitePageRoute ||
      isVisitorOnSpaceRoute) &&
    !EXCLUDED_TAB_ROUTES.some(route => pathname.includes(route));

  if (!override && !shouldShowDefaultTabs) {
    return null;
  }

  // The default (non-override) path delegates entirely to
  // `DefaultSectionTabs`, including whether to wrap itself in
  // `MobileShell.HeaderBar` - it may have no tabs to show (e.g. a Site
  // Page with no visible nav links) and needs to be able to render nothing
  // at all, not an empty bar. See the comment on `DefaultSectionTabs` for
  // why that can't be decided here instead.
  if (!override) {
    return <DefaultSectionTabs />;
  }

  return (
    <MobileShell.HeaderBar
      border="always"
      themeVars={
        isTopBarUnbranded || !isCustomColorTheme
          ? undefined
          : MOBILE_SECTION_TABS_THEME_VARS
      }
    >
      <Tabs.Root
        tabs={override.tabs}
        selectedValue={override.selectedValue}
        onValueChange={override.onValueChange}
        size="md"
      />
    </MobileShell.HeaderBar>
  );
};
